import React from "react";
import IChatbot from "../interface/iChatbot";
import IMessage, { IGenesysFile } from "../interface/IMessage";
import sendMessageToBackend from "../api/sendMessageToBackend";
import AvatarMia from "../assets/images/mia-kreis.png";
import formatDateToRelativeTimeDisplay from "../function/formatDateToRelativeTimeDisplay";
import { trackChatEvent } from "../utils/trackChatEvent";
import { deploymentIds } from "../utils/deploymentIds";
import { v4 as uuidv4 } from "uuid";
import isIGenesysFile from "../function/isIGenesysFile";
import debugLog from "../utils/debugLog";
import { reportAppError } from "../utils/reportAppError";
import {
  DEFAULT_BRAND,
  DEFAULT_ENVIRONMENT,
} from "../utils/validateChatbotConfig";
import type { ChatbotConfig, Brand } from "../lib/ChatbotConfig";
import {
  isBackendReady,
  isOnReceiveMessageSubscribed,
  markOnReceiveMessageSubscribed,
  disconnectFromBackend,
} from "../api/backendConnector";

// Status of loading the Genesys SDK, surfaced to the UI so it can show an
// error/retry hint when initialization fails.
export type GenesysStatus = "idle" | "loading" | "ready" | "error";

const localPreviewUrls = new WeakMap<File, string>();
const USER_MESSAGE_MERGE_WINDOW_IN_MS = 10000;

export interface IAppContext {
  minimizeBot: boolean;
  setMinimizeBot: React.Dispatch<React.SetStateAction<boolean>>;
  openChat: boolean;
  setOpenChat: React.Dispatch<React.SetStateAction<boolean>>;
  chatbot: IChatbot;
  setChatbot: React.Dispatch<React.SetStateAction<IChatbot>>;
  showMinimizeBotPopup: boolean;
  setShowMinimizeBotPopup: React.Dispatch<React.SetStateAction<boolean>>;
  botIsTyping: boolean;
  setBotIsTyping: React.Dispatch<React.SetStateAction<boolean>>;
  acceptDataProtectionNotice: boolean;
  setAcceptDataProtectionNotice: React.Dispatch<React.SetStateAction<boolean>>;
  chatInput: IMessage;
  setChatInput: React.Dispatch<React.SetStateAction<IMessage>>;
  handleSubmitMessage: (e: { preventDefault: () => void }) => Promise<void>;
  onReceiveMessage: () => void;
  updateMessageList: (message: IMessage, saveToLocalStorage: boolean) => void;
  rateChat: boolean;
  setRateChat: React.Dispatch<React.SetStateAction<boolean>>;
  getAgentAvatar: (message: IMessage) => string;
  getAgentName: (message: IMessage) => string;
  getLastAgentMessage: () => IMessage;
  handleMinimizeBot: () => void;
  setChatOpen: (state: boolean) => void;
  hideLauncher: boolean;
  clearConversation: () => void;
  startConversation: () => void;
  getDeploymentId: () => string;
  initializeApp: boolean;
  setInitializeApp: React.Dispatch<React.SetStateAction<boolean>>;
  initializeGenesys: boolean;
  setInitializeGenesys: React.Dispatch<React.SetStateAction<boolean>>;
  genesysStatus: GenesysStatus;
  setGenesysStatus: React.Dispatch<React.SetStateAction<GenesysStatus>>;
  genesysLoadAttempt: number;
  retryGenesysLoad: () => void;
  handleOpenChat: (state: boolean) => void;
  handleAcceptDataProtectionNotice: (state: boolean) => void;
  trackChatStarted: () => void;
  skipWelcomePage: boolean;
  brand: Brand;
}

export const AppContext = React.createContext<IAppContext>({} as IAppContext);

export interface IBotState {
  minimizeBot: boolean;
  showMinimizeBotPopup: boolean;
  openChat: boolean;
  acceptDataProtectionNotice: boolean;
}

const brandDisplayNames: Record<string, string> = {
  training: "WBS TRAINING",
  schools: "WBS SCHULEN",
  group: "WBS GRUPPE",
  academy: "WBS AKADEMIE",
};

const getLocalStorageBoolean = (
  key: keyof IBotState,
  defaultValue: boolean,
): boolean => {
  try {
    const item = localStorage.getItem(key);
    return item === null ? defaultValue : (JSON.parse(item) as boolean);
  } catch {
    return defaultValue;
  }
};

// Returns true when the user already has an active/persisted conversation, i.e.
// the chat was previously opened or real messages exist beyond the initial
// welcome message. In that case the Genesys SDK is loaded eagerly so returning
// users still receive messages and continue where they left off. First-time
// visitors get lazy loading (SDK loads on the first chat interaction), keeping
// the heavy Genesys resources off the initial page load (SEO / Core Web Vitals).
const hasPersistedConversation = (): boolean => {
  if (getLocalStorageBoolean("openChat", false)) return true;
  try {
    const stored = JSON.parse(localStorage.getItem("messageList") ?? "[]");
    return Array.isArray(stored) && stored.length > 1;
  } catch {
    return false;
  }
};

const createEmptyChatInput = (overrides: Partial<IMessage> = {}): IMessage => ({
  id: "",
  text: "",
  sender: "user",
  timeStamp: null,
  content: null,
  sending: false,
  agent: {
    avatar: "",
    name: "",
    nickname: "",
  },
  ...overrides,
});

const AppContextProvider = ({
  children,
  config,
}: {
  children: React.ReactNode;
  config: ChatbotConfig;
}): React.JSX.Element => {
  const channelRef = React.useRef<BroadcastChannel | null>(null);

  const [minimizeBot, setMinimizeBot] = React.useState<boolean>(
    getLocalStorageBoolean("minimizeBot", true),
  );

  // Mirrors `minimizeBot` so setChatOpen can read the current state without
  // being re-created on every toggle — the imperative controls handed to the
  // host page must stay stable.
  const minimizeBotRef = React.useRef<boolean>(minimizeBot);
  React.useEffect(() => {
    minimizeBotRef.current = minimizeBot;
  }, [minimizeBot]);

  const [showMinimizeBotPopup, setShowMinimizeBotPopup] =
    React.useState<boolean>(
      getLocalStorageBoolean("showMinimizeBotPopup", true),
    );

  const [openChat, setOpenChat] = React.useState<boolean>(
    getLocalStorageBoolean("openChat", false),
  );

  const [acceptDataProtectionNotice, setAcceptDataProtectionNotice] =
    React.useState<boolean>(
      getLocalStorageBoolean("acceptDataProtectionNotice", false),
    );

  const [rateChat, setRateChat] = React.useState<boolean>(false);

  const [chatbot, setChatbot] = React.useState<IChatbot>({
    messages: [],
    config: {},
  });

  const [botIsTyping, setBotIsTyping] = React.useState<boolean>(false);

  const [chatInput, setChatInput] = React.useState<IMessage>(createEmptyChatInput);
  const chatInputRef = React.useRef<IMessage>(chatInput);
  React.useEffect(() => {
    chatInputRef.current = chatInput;
  });

  const initialMessageRef = React.useRef<IMessage>({
    id: "initial-welcome-message",
    text: "",
    sender: "bot",
    timeStamp: new Date().toISOString(),
    content: null,
    sending: false,
    agent: {
      avatar: "",
      name: "Mia",
      nickname: "Mia",
    },
  });

  const [initializeApp, setInitializeApp] = React.useState<boolean>(false);

  const [initializeGenesys, setInitializeGenesys] =
    React.useState<boolean>(false);

  const [genesysStatus, setGenesysStatus] =
    React.useState<GenesysStatus>("idle");

  // Bumped to re-trigger the SDK load effect in App on retry.
  const [genesysLoadAttempt, setGenesysLoadAttempt] = React.useState<number>(0);

  // Reset the backend and re-attempt loading the SDK after a failure.
  const retryGenesysLoad = React.useCallback((): void => {
    disconnectFromBackend();
    setGenesysStatus("loading");
    // Ensure the SDK load is (re-)requested even if it was never triggered yet,
    // so retry always reloads — the App effect is gated on initializeGenesys.
    setInitializeGenesys(true);
    setGenesysLoadAttempt((attempt) => attempt + 1);
  }, []);

  const handleOpenChat = React.useCallback((state: boolean): void => {
    localStorage.setItem("openChat", JSON.stringify(state));
    setOpenChat(state);
    // Entering the actual chat needs the SDK — load it now if not already.
    if (state) setInitializeGenesys(true);
  }, []);

  const handleAcceptDataProtectionNotice = React.useCallback(
    (state: boolean): void => {
      localStorage.setItem("acceptDataProtectionNotice", JSON.stringify(state));
      setAcceptDataProtectionNotice(state);
    },
    [],
  );

  const trackChatStarted = React.useCallback((): void => {
    trackChatEvent("chat_started", config.onChatEvent);
  }, [config.onChatEvent]);

  // The two skip flags are *derived*, never written to state or localStorage.
  // Persisting them would make hasPersistedConversation() report an active
  // conversation on every first visit, which loads the Genesys SDK eagerly on
  // page load and undoes the lazy loading (SEO / Core Web Vitals).
  const skipWelcomePage = Boolean(config.skipWelcomePage);
  const skipDataProtectionNotice = Boolean(config.skipDataProtectionNotice);

  const effectiveOpenChat = skipWelcomePage || openChat;
  const effectiveAcceptDataProtectionNotice =
    skipDataProtectionNotice || acceptDataProtectionNotice;

  // With the notice skipped nobody ever clicks "accept", which is where
  // chat_started is normally tracked — so track it when the conversation view
  // is actually reached instead. trackChatEvent itself deduplicates per
  // session, so this fires once per session, as the accept click would.
  React.useEffect(() => {
    if (!skipDataProtectionNotice) return;
    if (minimizeBot || rateChat) return;
    if (!effectiveOpenChat || !effectiveAcceptDataProtectionNotice) return;

    trackChatStarted();
  }, [
    skipDataProtectionNotice,
    minimizeBot,
    rateChat,
    effectiveOpenChat,
    effectiveAcceptDataProtectionNotice,
    trackChatStarted,
  ]);

  const getBrandName = React.useCallback((): string => {
    return brandDisplayNames[config.brand] ?? "WBS";
  }, [config.brand]);

  const setMessageSendingState = React.useCallback(
    (messageId: string, sending: boolean): void => {
      setChatbot((prevChatbot: IChatbot) => ({
        ...prevChatbot,
        messages: prevChatbot.messages.map((message: IMessage) =>
          message.id === messageId ? { ...message, sending } : message,
        ),
      }));
    },
    [],
  );

  const markMessageAsSent = React.useCallback((messageId: string): void => {
    setMessageSendingState(messageId, false);
  }, [setMessageSendingState]);

  const serializeMessageContent = React.useCallback(
    (content: IMessage["content"]): IMessage["content"] => {
      if (!content) return null;

      if (isIGenesysFile(content)) return content;

      const cachedUrl = localPreviewUrls.get(content);
      const previewUrl =
        cachedUrl ||
        (typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(content)
          : "");

      if (!cachedUrl && previewUrl) {
        localPreviewUrls.set(content, previewUrl);
      }

      const previewFile: IGenesysFile = {
        id: content.name,
        filename: content.name,
        fileSize: content.size,
        mediaType: content.type,
        mime: content.type,
        url: previewUrl,
      };

      return previewFile;
    },
    [],
  );

  const persistMessageToLocalStorage = React.useCallback(
    (message: IMessage): void => {
      const messageList: IMessage[] = JSON.parse(
        localStorage.getItem("messageList") ?? "[]",
      );

      const uniqueMessages = messageList.filter(
        (storedMessage: IMessage) =>
          !(
            storedMessage.sender === message.sender &&
            storedMessage.timeStamp === message.timeStamp &&
            storedMessage.text === message.text
          ),
      );

      const persistedMessage: IMessage = {
        ...message,
        content: serializeMessageContent(message.content),
        sending: false,
      };

      const updatedList = [...uniqueMessages, persistedMessage];

      localStorage.setItem("messageList", JSON.stringify(updatedList));
      channelRef.current?.postMessage({ type: "NEW_MESSAGE", message: persistedMessage });
    },
    [serializeMessageContent],
  );

  const getMessageContentSignature = React.useCallback(
    (content: IMessage["content"]): string => {
      if (!content) return "";

      if (isIGenesysFile(content)) {
        return [content.filename, content.fileSize, content.mime].join("|");
      }

      return [content.name, content.size, content.type].join("|");
    },
    [],
  );

  const normalizeUserMessageText = React.useCallback(
    (message: IMessage): string => {
      const messageText = typeof message.text === "string" ? message.text : "";

      return message.content && !messageText.trim() ? "" : messageText;
    },
    [],
  );

  const isLocalPreviewMessage = React.useCallback((message: IMessage): boolean => {
    if (!message.content || !isIGenesysFile(message.content)) {
      return false;
    }

    if (
      typeof message.content.url === "string" &&
      message.content.url.startsWith("blob:")
    ) {
      return true;
    }

    return (
      (!message.content.url || message.content.url === "") &&
      message.content.id === message.content.filename
    );
  }, []);

  const shouldMergeUserMessages = React.useCallback(
    (existingMessage: IMessage, incomingMessage: IMessage): boolean => {
      if (existingMessage.sender !== "user" || incomingMessage.sender !== "user") {
        return false;
      }

      if (
        normalizeUserMessageText(existingMessage) !==
        normalizeUserMessageText(incomingMessage)
      ) {
        return false;
      }

      if (
        getMessageContentSignature(existingMessage.content) !==
        getMessageContentSignature(incomingMessage.content)
      ) {
        return false;
      }

      const existingTimestamp = new Date(existingMessage.timeStamp ?? "").getTime();
      const incomingTimestamp = new Date(incomingMessage.timeStamp ?? "").getTime();

      if (!Number.isFinite(existingTimestamp) || !Number.isFinite(incomingTimestamp)) {
        return isLocalPreviewMessage(existingMessage);
      }

      return (
        isLocalPreviewMessage(existingMessage) ||
        Math.abs(existingTimestamp - incomingTimestamp) <=
        USER_MESSAGE_MERGE_WINDOW_IN_MS
      );
    },
    [getMessageContentSignature, isLocalPreviewMessage, normalizeUserMessageText],
  );

  const updateMessageList = React.useCallback(
    (message: IMessage, saveToLocalStorage: boolean): void => {
      if (message.sender === "bot" && !message.text) return;

      const messageList: IMessage[] = JSON.parse(
        localStorage.getItem("messageList") ?? "[]",
      );

      const uniqueMessages: IMessage[] = messageList.filter(
        (msg: IMessage) =>
          !(
            msg.sender === message.sender &&
            msg.timeStamp === message.timeStamp &&
            msg.text === message.text
          ),
      );

      const matchingUserMessage = [...messageList]
        .reverse()
        .find((storedMessage: IMessage) =>
          shouldMergeUserMessages(storedMessage, message),
        );

      const normalizedText = normalizeUserMessageText(message);

      const newMessage: IMessage = {
        id: matchingUserMessage?.id ?? message.id,
        text: normalizedText,
        sender: message.sender,
        timeStamp: message.timeStamp,
        content: message.content,
        sending: false,
        agent: message.agent,
      };

      const updatedList: IMessage[] = matchingUserMessage
        ? messageList.map((storedMessage: IMessage) =>
            storedMessage.id === matchingUserMessage.id ? newMessage : storedMessage,
          )
        : [...uniqueMessages, newMessage];

      const userMessageCount: number = updatedList.filter(
        (message: IMessage) => message.sender === "user",
      ).length;

      if (message.sender === "user" && userMessageCount === 1) {
        trackChatEvent("contact_first_message", config.onChatEvent);
      }

      if (saveToLocalStorage) {
        localStorage.setItem("messageList", JSON.stringify(updatedList));
        channelRef.current?.postMessage({ type: "NEW_MESSAGE", message: newMessage });
      }

      const formattedList: IMessage[] = updatedList.map((msg): IMessage => {
        const formattedTime = formatDateToRelativeTimeDisplay(
          msg.timeStamp as string,
        );
        return { ...msg, timeStamp: formattedTime };
      });

      const filteredList: IMessage[] = formattedList.map((msg, index, arr) => {
        const next = arr[index + 1];
        if (
          next &&
          msg.sender === next.sender &&
          msg.timeStamp === next.timeStamp
        ) {
          return { ...msg, timeStamp: null };
        }
        return msg;
      });

      setChatbot((prev: IChatbot) => ({ ...prev, messages: filteredList }));
    },
    [config.onChatEvent, normalizeUserMessageText, shouldMergeUserMessages],
  );

  const addSystemMessage = React.useCallback(
    (text: string): void => {
      const rawSystemMessage: IMessage = {
        id: uuidv4(),
        text,
        sender: "system",
        timeStamp: new Date().toISOString(),
        content: null,
        sending: false,
        agent: {
          avatar: "",
          name: "",
          nickname: "",
        },
      };

      const storedMessages: IMessage[] = JSON.parse(
        localStorage.getItem("messageList") ?? "[]",
      );

      localStorage.setItem(
        "messageList",
        JSON.stringify([...storedMessages, rawSystemMessage]),
      );
      channelRef.current?.postMessage({ type: "NEW_MESSAGE", message: rawSystemMessage });

      const formattedSystemMessage: IMessage = {
        ...rawSystemMessage,
        timeStamp: formatDateToRelativeTimeDisplay(rawSystemMessage.timeStamp),
      };

      setChatbot((prevChatbot: IChatbot): IChatbot => ({
        ...prevChatbot,
        messages: [...prevChatbot.messages, formattedSystemMessage],
      }));
    },
    [],
  );

  const sendMessage = React.useCallback(async (): Promise<void> => {
    const { id, text, content } = chatInputRef.current;
    const messageId = id || uuidv4();
    const timeStamp = new Date().toISOString();
    const displayTimeStamp = formatDateToRelativeTimeDisplay(timeStamp);

    const newMessage: IMessage = {
      id: messageId,
      text,
      sender: "user",
      timeStamp: displayTimeStamp,
      content,
      sending: true,
      agent: {
        avatar: "",
        name: "",
        nickname: "",
      },
    };

    setChatbot((prevChatbot: IChatbot): IChatbot => {
      const updatedMessages = [...prevChatbot.messages, newMessage];

      return {
        ...prevChatbot,
        messages: updatedMessages.map(
          (message: IMessage, index: number, arr: IMessage[]) => {
            const nextMessage = arr[index + 1];

            if (
              nextMessage &&
              message.sender === nextMessage.sender &&
              message.timeStamp === nextMessage.timeStamp
            ) {
              return { ...message, timeStamp: null };
            }

            return message;
          },
        ),
      };
    });

    const sendSucceeded = await sendMessageToBackend({
      ...newMessage,
      timeStamp,
    });

    if (sendSucceeded) {
      persistMessageToLocalStorage({
        ...newMessage,
        timeStamp,
        sending: false,
      });
      markMessageAsSent(messageId);
      return;
    }

    setMessageSendingState(messageId, false);
    addSystemMessage(
      newMessage.content
        ? "Datei konnte nicht gesendet werden. Bitte versuche es erneut."
        : "Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.",
    );
    reportAppError({
      scope: "genesys.context.sendMessage",
      metadata: {
        hasAttachment: Boolean(newMessage.content),
        sender: newMessage.sender,
      },
    });
  }, [
    addSystemMessage,
    markMessageAsSent,
    persistMessageToLocalStorage,
    setMessageSendingState,
  ]);

  const getAgentData = React.useCallback((data: any) => {
    const message = data.messages?.[0];
    if (!message) return { avatar: "", name: "Mia", nickname: "Mia" };

    if (message.direction === "Inbound") return { avatar: "", name: "", nickname: "" };

    const from = message.channel?.from;
    if (!from || Object.keys(from).length === 0) {
      return { avatar: "", name: "Mia", nickname: "Mia" };
    }

    return {
      avatar: from.avatar,
      name: from.name,
      nickname: from.nickname,
    };
  }, []);

  const getSender = React.useCallback(
    (data: any): "user" | "bot" | "system" | "agent" => {
      const message = data.messages?.[0];
      if (!message) return "system";

      if (message.direction === "Inbound") return "user";

      if (message.direction === "Outbound") {
        if (message.originatingEntity === "Human") return "agent";
        return "bot";
      }

      return "system";
    },
    [],
  );

  const handleTextMessage = React.useCallback(
    (data: any): void => {
      const message = data.messages?.[0];
      if (!message) return;

      if (message.originatingEntity === "Bot") {
        debugLog("Bot message");
      }

      const incomingAttachment = message.content
        ? message.content[0]?.attachment ?? null
        : null;
      const rawIncomingText =
        typeof message.text === "string" ? message.text : "";

      const incomingText =
        incomingAttachment && !rawIncomingText.trim() ? "" : rawIncomingText;

      if (!incomingText && !incomingAttachment) {
        debugLog("Skipping empty message from Genesys SDK");
        return;
      }

      const customMessage: IMessage = {
        id: uuidv4(),
        text: incomingText,
        sender: getSender(data),
        timeStamp: message.channel?.time ?? new Date().toISOString(),
        content: incomingAttachment,
        sending: false,
        agent: getAgentData(data),
      };

      updateMessageList(customMessage, true);
    },
    [getAgentData, getSender, updateMessageList],
  );

  const isCoBrowseOffer = (data: any): boolean =>
    data.messages?.[0]?.events?.[0]?.coBrowse?.type === "Offering";

  const onReceiveMessage = React.useCallback((): void => {
    try {
      Genesys("subscribe", "MessagingService.ready", () => {
        if (isOnReceiveMessageSubscribed()) return;
        markOnReceiveMessageSubscribed();
        try {
          Genesys(
            "subscribe",
            "MessagingService.messagesReceived",
            ({ data }: any) => {
              !isCoBrowseOffer(data) && handleTextMessage(data);
            },
          );
        } catch (error) {
          reportAppError({ scope: "genesys.context.onReceiveMessage.subscribe", error });
        }
      });
    } catch (error) {
      reportAppError({ scope: "genesys.context.onReceiveMessage", error });
    }
  }, [handleTextMessage]);

  const handleSubmitMessage = React.useCallback(
    async (e: { preventDefault: () => void }): Promise<void> => {
      e.preventDefault();

      const { text, content, sending } = chatInputRef.current;
      if ((!text && !content) || sending) return;

      setChatInput(createEmptyChatInput({ sending: true }));

      try {
        await sendMessage();
      } finally {
        setChatInput((currentInput: IMessage): IMessage =>
          currentInput.sending
            ? { ...currentInput, sending: false }
            : currentInput,
        );
      }
    },
    [sendMessage],
  );

  const getAgentAvatar = React.useCallback((message: IMessage): string => {
    if (message.agent.avatar === "") return AvatarMia;
    return message.agent.avatar;
  }, []);

  const getAgentName = React.useCallback((message: IMessage): string => {
    if (message.agent && message.agent.name !== "") return message.agent.name;
    return "Mia";
  }, []);

  const getLastAgentMessage = React.useCallback((): IMessage => {
    if (chatbot.messages.length === 0) return initialMessageRef.current;

    for (let index = chatbot.messages.length - 1; index >= 0; index--) {
      const message = chatbot.messages[index];
      if (message.sender === "agent") return message;
    }

    return initialMessageRef.current;
  }, [chatbot.messages]);

  const handleMinimizeBot = React.useCallback(() => {
    trackChatEvent("chat_requested", config.onChatEvent);

    // First interaction with the bubble → load the Genesys SDK (lazy load).
    setInitializeGenesys(true);

    setMinimizeBot((prev) => {
      const next = !prev;
      debugLog("Toggling minimizeBot state:", next);
      localStorage.setItem("minimizeBot", JSON.stringify(next));
      return next;
    });
    localStorage.setItem("showMinimizeBotPopup", JSON.stringify(false));
    setShowMinimizeBotPopup(false);
  }, [config.onChatEvent]);

  // Opening/closing from outside the widget — only reachable when the host page
  // took over the trigger (`hideLauncher`), via mountChatbot's handle. Kept
  // separate from handleMinimizeBot on purpose: the bubble's behaviour, down to
  // its tracking, stays exactly as it was.
  //
  // Idempotent, so a host page may call open() on every click of its own
  // trigger; only a real transition to "open" counts as a chat request.
  const setChatOpen = React.useCallback(
    (state: boolean): void => {
      const nextMinimize = !state;

      if (minimizeBotRef.current === nextMinimize) return;

      if (state) {
        trackChatEvent("chat_requested", config.onChatEvent);
        setInitializeGenesys(true);
      }

      debugLog("Toggling minimizeBot state:", nextMinimize);

      // Kept in sync eagerly so repeated calls within the same tick (e.g. a
      // flushed click queue) are collapsed before React re-renders.
      minimizeBotRef.current = nextMinimize;
      localStorage.setItem("minimizeBot", JSON.stringify(nextMinimize));
      setMinimizeBot(nextMinimize);

      localStorage.setItem("showMinimizeBotPopup", JSON.stringify(false));
      setShowMinimizeBotPopup(false);
    },
    [config.onChatEvent],
  );

  const clearConversation = React.useCallback(async () => {
    Genesys(
      "command",
      "MessagingService.clearConversation",
      {},
      function () {
        debugLog("Conversation cleared");
        localStorage.removeItem("messageList");
        updateMessageList(initialMessageRef.current, true);
        handleOpenChat(false);
        handleAcceptDataProtectionNotice(false);
      },
      function () {
        reportAppError({ scope: "genesys.context.clearConversation" });
        addSystemMessage(
          "Konversation konnte nicht gelöscht werden. Bitte versuche es erneut.",
        );
      },
    );
  }, [
    addSystemMessage,
    updateMessageList,
    handleOpenChat,
    handleAcceptDataProtectionNotice,
  ]);

  const startConversation = React.useCallback(() => {
    debugLog("startConversation...");
    setInitializeGenesys(true);

    const doFetchHistory = () => {
      Genesys(
        "command",
        "MessagingService.fetchHistory",
        {},
        (history: any) => {
          if (!history || history.length === 0) {
            debugLog("No conversation found, starting a new one...");

            Genesys(
              "command",
              "MessagingService.startConversation",
              {},
              () => debugLog("Conversation started"),
              (err: any) =>
                reportAppError({
                  scope: "genesys.context.startConversation.start",
                  error: err,
                }),
            );
          } else {
            debugLog("Conversation already exists, skipping startConversation");
          }
        },
        (err: any) => {
          reportAppError({
            scope: "genesys.context.startConversation.fetchHistory",
            error: err,
          });
        },
      );
    };

    if (isBackendReady()) {
      doFetchHistory();
    } else {
      Genesys("subscribe", "MessagingService.ready", doFetchHistory);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDeploymentId = React.useCallback((): string => {
    if (config.deploymentId) return config.deploymentId;

    // Defaulted by validateChatbotConfig before the provider ever sees the
    // config — repeated here only so a direct provider render cannot fall
    // through to `undefined`.
    const env = config.environment ?? DEFAULT_ENVIRONMENT;
    const brand = config.brand ?? DEFAULT_BRAND;

    debugLog(`Use brand: ${brand}, environment: ${env}`);

    return deploymentIds[brand]?.[env] ?? deploymentIds.training.prod;
  }, [config.brand, config.deploymentId, config.environment]);

  // Initialize app immediately — no trust check needed in package mode.
  // The Genesys SDK itself is NOT loaded here; it is loaded lazily on the first
  // chat interaction (see handleMinimizeBot / handleOpenChat) to keep its heavy
  // resources off the initial page load. Returning users with an active
  // conversation are the exception and load it eagerly.
  React.useEffect(() => {
    initialMessageRef.current = {
      ...initialMessageRef.current,
      text: `Herzlich willkommen bei ${getBrandName()}!\n\nSchön, dass du da bist. Ich bin Mia, deine digitale Assistentin. Darf ich dir einen freien Bildungsexperten suchen?`,
    };

    setInitializeApp(true);

    if (hasPersistedConversation()) {
      setInitializeGenesys(true);
    }
  }, [getBrandName]);

  // Load old messages from localStorage
  React.useEffect(() => {
    if (!initializeApp) return;

    debugLog("Loading old messages from local storage...");

    let rawList: IMessage[] = JSON.parse(localStorage.getItem("messageList") ?? "[]");

    if (rawList.length === 0) {
      rawList = [initialMessageRef.current];
      localStorage.setItem("messageList", JSON.stringify(rawList));
      debugLog("No Messages found. Added initial message to local storage");
    }

    const formattedList = rawList.map((message): IMessage => ({
      ...message,
      timeStamp: formatDateToRelativeTimeDisplay(message.timeStamp as string),
    }));

    const filteredList = formattedList.map((message, index, arr): IMessage => {
      const next = arr[index + 1];
      if (next && message.sender === next.sender && message.timeStamp === next.timeStamp) {
        return { ...message, timeStamp: null };
      }
      return message;
    });

    setChatbot((prev) => ({ ...prev, messages: filteredList }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeApp]);

  // Keep a ref to the latest updateMessageList so the BroadcastChannel handler
  // never captures a stale closure, and the channel itself is recreated only
  // when the component mounts/unmounts — not on every message.
  const updateMessageListRef = React.useRef(updateMessageList);
  React.useEffect(() => {
    updateMessageListRef.current = updateMessageList;
  });

  // Multitab sync
  React.useEffect(() => {
    channelRef.current = new BroadcastChannel("chat-sync");

    channelRef.current.onmessage = (event) => {
      if (event.data?.type === "NEW_MESSAGE") {
        debugLog(
          "Received new message from another tab:",
          event.data.message,
        );

        updateMessageListRef.current(event.data.message, false);
      }
    };

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, []);

  const value: IAppContext = React.useMemo(
    () => ({
      minimizeBot,
      setMinimizeBot,
      // The skip flags take effect here, for every consumer at once: the page
      // switch in maximizedChat, the navbar and the back arrow all read these.
      openChat: effectiveOpenChat,
      setOpenChat,
      chatbot,
      setChatbot,
      showMinimizeBotPopup,
      setShowMinimizeBotPopup,
      botIsTyping,
      setBotIsTyping,
      acceptDataProtectionNotice: effectiveAcceptDataProtectionNotice,
      setAcceptDataProtectionNotice,
      chatInput,
      setChatInput,
      handleSubmitMessage,
      onReceiveMessage,
      updateMessageList,
      rateChat,
      setRateChat,
      getAgentAvatar,
      getAgentName,
      getLastAgentMessage,
      handleMinimizeBot,
      setChatOpen,
      hideLauncher: Boolean(config.hideLauncher),
      clearConversation,
      startConversation,
      getDeploymentId,
      initializeApp,
      setInitializeApp,
      initializeGenesys,
      setInitializeGenesys,
      genesysStatus,
      setGenesysStatus,
      genesysLoadAttempt,
      retryGenesysLoad,
      handleOpenChat,
      handleAcceptDataProtectionNotice,
      trackChatStarted,
      skipWelcomePage,
      brand: config.brand,
    }),
    [
      effectiveAcceptDataProtectionNotice,
      botIsTyping,
      chatInput,
      chatbot,
      getAgentAvatar,
      getAgentName,
      getLastAgentMessage,
      handleSubmitMessage,
      minimizeBot,
      onReceiveMessage,
      effectiveOpenChat,
      rateChat,
      showMinimizeBotPopup,
      updateMessageList,
      handleMinimizeBot,
      setChatOpen,
      config.hideLauncher,
      clearConversation,
      startConversation,
      getDeploymentId,
      initializeApp,
      setInitializeApp,
      initializeGenesys,
      setInitializeGenesys,
      genesysStatus,
      genesysLoadAttempt,
      retryGenesysLoad,
      handleOpenChat,
      handleAcceptDataProtectionNotice,
      trackChatStarted,
      skipWelcomePage,
      config.brand,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContextProvider;
